import type { SessionAccess, SessionEvent } from '@/types';

export interface OfficialSourceOption {
  user_id: number;
  label: string;
}

export function resolveOfficialUserIds(
  access?: Pick<SessionAccess, 'official_source_user_id' | 'official_source_user_ids'> | null,
  sessionOwnerId?: number | null
): number[] {
  if (access?.official_source_user_ids?.length) {
    return access.official_source_user_ids.map(Number);
  }
  const single = access?.official_source_user_id ?? sessionOwnerId;
  return single != null ? [Number(single)] : [];
}

export function resolveOfficialUserId(
  access?: Pick<SessionAccess, 'official_source_user_id' | 'official_source_user_ids'> | null,
  sessionOwnerId?: number | null
): number | undefined {
  const ids = resolveOfficialUserIds(access, sessionOwnerId);
  return ids[0];
}

export function isUserOfficialSource(
  userId: number | undefined,
  officialUserIds: number | number[] | undefined
): boolean {
  if (userId == null || officialUserIds == null) return false;
  const ids = Array.isArray(officialUserIds) ? officialUserIds : [officialUserIds];
  return ids.some((id) => Number(userId) === Number(id));
}

/** Whether this mark is the designated official reference (not merely marker_role). */
export function isOfficialReferenceEvent(
  event: SessionEvent,
  officialUserIds?: number | number[]
): boolean {
  const ids = Array.isArray(officialUserIds)
    ? officialUserIds
    : officialUserIds != null
      ? [officialUserIds]
      : [];
  if (ids.length) {
    return ids.some((id) => Number(event.created_by_user_id) === Number(id));
  }
  return event.marker_role !== 'participant';
}

export function resolveOfficialSourceDisplayName(
  officialUserId: number | undefined,
  options: {
    currentUserId?: number;
    sessionOwnerId?: number;
    members?: Array<{ user_id: number; label: string }>;
  }
): string {
  if (officialUserId == null) return '—';
  if (options.currentUserId != null && Number(options.currentUserId) === Number(officialUserId)) {
    return '您';
  }
  const member = options.members?.find((m) => m.user_id === officialUserId);
  if (member?.label) return member.label;
  if (options.sessionOwnerId != null && Number(options.sessionOwnerId) === Number(officialUserId)) {
    return '建立者';
  }
  return `用戶 ${officialUserId}`;
}

export function resolveOfficialSourceDisplayNames(
  officialUserIds: number[],
  options: {
    currentUserId?: number;
    sessionOwnerId?: number;
    members?: Array<{ user_id: number; label: string }>;
  }
): string {
  if (!officialUserIds.length) return '—';
  return officialUserIds
    .map((id) => resolveOfficialSourceDisplayName(id, options))
    .join('、');
}
